import { StyleSheet } from 'react-native';

// Design tokens from prototypes
export const webColors = {
  // Primary brand colors
  primary: '#EC4899',
  primaryDark: '#DB2777',
  primaryLight: '#fdf2f8',
  primaryGradient: 'linear-gradient(135deg, #EC4899 0%, #f472b6 100%)',

  // Secondary colors
  secondary: '#2557a7',
  secondaryLight: '#3b82f6',
  secondaryLighter: '#dbeafe',

  // Neutrals
  background: '#f8fafc',
  backgroundAlt: '#f9fafb',
  cardBackground: '#ffffff',

  // Text colors
  textPrimary: '#1a1a1a',
  textSecondary: '#6b7280',
  textMuted: 'gray',
  textLink: '#5B9FF9',

  // Border colors
  border: '#e5e7eb',
  borderLight: '#f3f4f6',
  borderFocus: '#EC4899',

  // Status colors
  success: '#22c55e',
  successLight: '#dcfce7',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  error: '#ef4444',
  errorLight: '#fee2e2',
  info: '#3b82f6',
  infoLight: '#dbeafe',
};

export const webSpacing = {
  navbarHeight: 64,
  containerMaxWidth: 1400,
  containerPadding: 40,
  sectionGap: 24,
  cardPadding: 24,
  inputPadding: 14,
};

// Responsive breakpoints (px). "Mobile" = viewport width below `mobile`.
export const breakpoints = {
  mobile: 768,
  smallMobile: 480,
};

export const webBorderRadius = {
  small: 8,
  medium: 12,
  large: 16,
  pill: 22,
  circle: 50,
};

export const webShadows = {
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  cardHover: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
  },
  button: {
    shadowColor: '#EC4899',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 15,
    elevation: 4,
  },
  modal: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.15,
    shadowRadius: 30,
    elevation: 10,
  },
};

export const webTypography = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  heading1: { fontSize: 32, fontWeight: '700', lineHeight: 40 },
  heading2: { fontSize: 28, fontWeight: '700', lineHeight: 36 },
  heading3: { fontSize: 22, fontWeight: '700', lineHeight: 28 },
  heading4: { fontSize: 18, fontWeight: '600', lineHeight: 24 },
  body: { fontSize: 16, fontWeight: '400', lineHeight: 24 },
  bodySmall: { fontSize: 14, fontWeight: '400', lineHeight: 20 },
  caption: { fontSize: 13, fontWeight: '400', lineHeight: 18 },
  label: { fontSize: 14, fontWeight: '600', lineHeight: 20 },
};

// Reusable web styles
export const webStyles = StyleSheet.create({
  // Layout
  pageContainer: {
    flex: 1,
    backgroundColor: webColors.background,
    minHeight: '100vh',
  },
  mainContainer: {
    maxWidth: webSpacing.containerMaxWidth,
    marginHorizontal: 'auto',
    paddingHorizontal: webSpacing.containerPadding,
    paddingVertical: 32,
    width: '100%',
  },

  // Cards
  card: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    padding: webSpacing.cardPadding,
    ...webShadows.card,
  },

  // Form elements
  input: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 16,
    paddingVertical: webSpacing.inputPadding,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  inputFocused: {
    borderColor: webColors.borderFocus,
    backgroundColor: webColors.cardBackground,
  },

  // Buttons
  buttonPrimary: {
    backgroundColor: webColors.primary,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  buttonPrimaryText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
  buttonSecondary: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 2,
    borderColor: webColors.primary,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: webBorderRadius.medium,
  },
  buttonSecondaryText: {
    color: webColors.primary,
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },
  buttonGhost: {
    backgroundColor: webColors.backgroundAlt,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: webBorderRadius.medium,
  },
  buttonGhostText: {
    color: webColors.textSecondary,
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'center',
  },

  // Typography
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textSecondary,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
  },

  // Links
  link: {
    color: webColors.primary,
    fontWeight: '600',
    textDecorationLine: 'none',
  },
  linkHover: {
    textDecorationLine: 'underline',
  },
});
